import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import React, { useMemo, useEffect, useRef } from "react";
import styled from "styled-components";

import {
  CARC_CODES,
  GROUP_CODES,
  RARC_CODES,
  formatAdjustment,
} from "../../constants/adjustmentCodes";
import { parseDate } from "../../helpers/date";
import {
  IClaim,
  ILineItem,
  ILineItemAdjustment,
  IMatched837Claim,
} from "../../types/claim";
import { PrecalculatedAllowablesContent } from "../PrecalculatedAllowablesModal/PrecalculatedAllowablesContent";
import { AIInsightsTabContent } from "./AIInsightsTabContent";
import { EstimateComparisonTab } from "./EstimateComparisonTab";
import { SimilarClaimsSection } from "./SimilarClaimsSection";

interface IClaimDetailsModalProps {
  visible: boolean;
  onHide: () => void;
  claim: IClaim;
  accountId: string;
  onNavigateToClaim?: (claimId: string) => void;
  onBackToClaim?: () => void;
  isNavigating?: boolean;
}

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailRow = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  min-width: 150px;
  margin-right: 0.75rem;
  color: #495057;
`;

const DetailValue = styled.div`
  color: #212529;
  flex: 1;
`;

const PlaceholderText = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

const ScrollableTabContent = styled.div`
  height: 60vh;
  max-height: 650px;
  overflow-y: auto;
  padding: 0;
`;

const InfoIcon = styled.i`
  font-size: 0.75rem;
  color: #6c757d;
  margin-left: 4px;
  cursor: help;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: #495057;
  }
`;

const GlossarySection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const GlossaryTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const GlossaryItem = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  line-height: 1.4;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GlossaryCode = styled.span`
  font-weight: 600;
  min-width: 60px;
  color: #212529;
`;

const GlossaryDescription = styled.span`
  color: #495057;
  flex: 1;
`;

const AdjustmentsCell = styled.div`
  font-size: 0.85rem;
  line-height: 1.4;
`;

const AdjustmentLine = styled.div`
  white-space: nowrap;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackButton = styled(Button)`
  &.p-button-text {
    padding: 0.25rem 0.5rem;
  }
`;

const ModalContentWrapper = styled.div`
  position: relative;
`;

const PATIENT_CLASS_STYLES: Record<
  string,
  { bg: string; color: string; label: string }
> = {
  INPATIENT: { bg: "#e8eaf6", color: "#3f51b5", label: "Inpatient" },
  OUTPATIENT: { bg: "#e8f5e9", color: "#2e7d32", label: "Outpatient" },
  UNKNOWN: { bg: "#f5f5f5", color: "#757575", label: "Unknown" },
};

function PatientClassBadge({ patientClass }: { patientClass: string }) {
  const style =
    PATIENT_CLASS_STYLES[patientClass] || PATIENT_CLASS_STYLES.UNKNOWN;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "4px",
        fontSize: "0.85rem",
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
      }}
    >
      {style.label}
    </span>
  );
}

function formatDollars(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function InpatientClaimSummary({ claim }: { claim: IClaim }) {
  const summary = claim.claimSummaryData;
  const adjustments = summary?.adjustments || [];

  const sumAdjustments = (group: string, code?: string): number => {
    return adjustments
      .filter(
        (a) => a.group === group && (code === undefined || a.code === code)
      )
      .reduce((sum, a) => sum + a.amount, 0);
  };

  const contractAdjustment = sumAdjustments("CO");
  const deductible = sumAdjustments("PR", "1");
  const coinsurance = sumAdjustments("PR", "2");
  const copayment = sumAdjustments("PR", "3");

  // Build columns dynamically based on available data
  const columns: { header: string; value: string }[] = [
    {
      header: "Total Charges",
      value:
        claim.chargeAmount !== null
          ? formatDollars(claim.chargeAmount / 100)
          : "-",
    },
  ];

  columns.push({ header: "DRG", value: claim.drgCode || "-" });
  columns.push({
    header: "DRG Weight",
    value: summary?.drgWeight || "-",
  });
  columns.push({
    header: "DRG Amount",
    value: summary?.drgAmount != null ? formatDollars(summary.drgAmount) : "-",
  });
  columns.push({
    header: "LOS (Days)",
    value: summary?.lengthOfStay != null ? String(summary.lengthOfStay) : "-",
  });
  columns.push({
    header: "Covered Charges",
    value:
      summary?.coveredAmount != null
        ? formatDollars(summary.coveredAmount)
        : "-",
  });
  columns.push({
    header: "Contract Adj",
    value: contractAdjustment !== 0 ? formatDollars(contractAdjustment) : "-",
  });
  columns.push({
    header: "Deductible",
    value: deductible !== 0 ? formatDollars(deductible) : "-",
  });
  columns.push({
    header: "Coinsurance",
    value: coinsurance !== 0 ? formatDollars(coinsurance) : "-",
  });
  columns.push({
    header: "Copay",
    value: copayment !== 0 ? formatDollars(copayment) : "-",
  });
  columns.push({
    header: "Payment",
    value: formatDollars(claim.claimAmount / 100),
  });

  return (
    <DataTable
      value={[Object.fromEntries(columns.map((c) => [c.header, c.value]))]}
      style={{ fontSize: "0.9rem" }}
    >
      {columns.map((col) => (
        <Column key={col.header} field={col.header} header={col.header} />
      ))}
    </DataTable>
  );
}

const FREQUENCY_CODE_LABELS: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  "1": { label: "Original", bg: "#e3f2fd", color: "#1565c0" },
  "7": { label: "Replacement", bg: "#fff3e0", color: "#e65100" },
  "8": { label: "Voided", bg: "#ffebee", color: "#c62828" },
};

function getAuthoritativeReason(
  claim: IMatched837Claim,
  allClaims: IMatched837Claim[]
): string | null {
  if (!claim.isAuthoritative) return null;

  const hasReplacements = allClaims.some((c) => c.frequencyCode === "7");
  const hasVoids = allClaims.some((c) => c.frequencyCode === "8");
  const originals = allClaims.filter((c) => c.frequencyCode === "1");

  if (claim.frequencyCode === "7") {
    if (hasVoids) return "Latest replacement (voids excluded)";
    return "Replacement supersedes original";
  }

  if (originals.length > 1 && !hasReplacements) {
    return "Latest original submission";
  }

  if (allClaims.length === 1) return null; // Only one claim, no need to explain
  return "Selected as authoritative";
}

function Matched837ClaimsSection({ claims }: { claims: IMatched837Claim[] }) {
  // Sort: authoritative first, then voided last
  const sortedClaims = useMemo(
    () =>
      [...claims].sort((a, b) => {
        if (a.isAuthoritative !== b.isAuthoritative)
          return a.isAuthoritative ? -1 : 1;
        if (a.frequencyCode === "8" && b.frequencyCode !== "8") return 1;
        if (a.frequencyCode !== "8" && b.frequencyCode === "8") return -1;
        return 0;
      }),
    [claims]
  );

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3
        style={{
          margin: "0 0 0.75rem 0",
          fontSize: "16px",
          fontWeight: 600,
        }}
      >
        837 Claim Submission Data
      </h3>
      {sortedClaims.map((claim, idx) => {
        const freqStyle =
          FREQUENCY_CODE_LABELS[claim.frequencyCode] ||
          FREQUENCY_CODE_LABELS["1"];
        const isVoided = claim.frequencyCode === "8";
        const authReason = getAuthoritativeReason(claim, claims);

        return (
          <div
            key={idx}
            style={{
              marginBottom: "1rem",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              overflow: "hidden",
              opacity: isVoided ? 0.6 : 1,
            }}
          >
            {/* Header bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 0.75rem",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e9ecef",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  backgroundColor: freqStyle.bg,
                  color: freqStyle.color,
                }}
              >
                {freqStyle.label}
              </span>
              {claim.isAuthoritative && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.35rem",
                    padding: "0.15rem 0.5rem",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                  }}
                >
                  Authoritative
                  {authReason && (
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        opacity: 0.8,
                      }}
                    >
                      ({authReason})
                    </span>
                  )}
                </span>
              )}
              <span
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  marginLeft: "auto",
                }}
              >
                Claim #{claim.claimNumber}
              </span>
            </div>

            {/* Details grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "0.5rem 1.5rem",
                padding: "0.75rem",
                fontSize: "0.85rem",
                textDecoration: isVoided ? "line-through" : "none",
              }}
            >
              <div>
                <span style={{ color: "#6c757d" }}>Service Date</span>
                <div style={{ fontWeight: 500 }}>
                  {claim.serviceDate || "-"}
                </div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Total Charges</span>
                <div style={{ fontWeight: 500 }}>
                  {formatDollars(claim.chargeAmount)}
                </div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Type of Bill</span>
                <div style={{ fontWeight: 500 }}>{claim.typeOfBill || "-"}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Patient Class</span>
                <div style={{ fontWeight: 500 }}>{claim.patientClass}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>DRG Code</span>
                <div style={{ fontWeight: 500 }}>{claim.drgCode || "-"}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Provider NPI</span>
                <div style={{ fontWeight: 500 }}>
                  {claim.providerNPI || "-"}
                </div>
              </div>
            </div>

            {/* Service lines table */}
            {claim.serviceLines.length > 0 && (
              <div style={{ padding: "0 0.75rem 0.75rem" }}>
                <DataTable
                  value={claim.serviceLines}
                  size="small"
                  style={{ fontSize: "0.85rem" }}
                >
                  <Column field="code" header="Code" />
                  <Column
                    field="revenueCode"
                    header="Rev Code"
                    body={(row) => row.revenueCode || "-"}
                  />
                  <Column
                    field="modifier"
                    header="Modifier"
                    body={(row) => row.modifier || "-"}
                  />
                  <Column field="quantity" header="Qty" />
                  <Column
                    field="chargeAmount"
                    header="Charge"
                    body={(row) =>
                      row.chargeAmount != null
                        ? formatDollars(row.chargeAmount)
                        : "-"
                    }
                  />
                </DataTable>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Modal component to display comprehensive claim details
 * with three tabs: Claim Details, Allowable Calculations, and AI
 */
export function ClaimDetailsModal({
  visible,
  onHide,
  claim,
  accountId,
  onNavigateToClaim,
  onBackToClaim,
  isNavigating = false,
}: IClaimDetailsModalProps) {
  // Ref for scrollable content to auto-scroll to top on claim change
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when claim changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [claim.id]);

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Format date helper
  const formatDate = (date: Date | null): string => {
    return date ? parseDate(date) : "-";
  };

  // Line items data
  const lineItems: ILineItem[] = useMemo(() => {
    return claim.lineItems || [];
  }, [claim.lineItems]);

  // Line item templates
  const currencyBodyTemplate = (rowData: ILineItem, field: keyof ILineItem) => {
    const value = rowData[field];
    if (typeof value === "number") {
      return formatCurrency(Math.round(value * 100)); // Convert dollars to cents
    }
    return "-";
  };

  // Get deductible amount (PR-1) from adjustments
  const getDeductibleAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "1"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get coinsurance amount (PR-2) from adjustments
  const getCoinsuranceAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "2"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get copay amount (PR-3) from adjustments
  const getCopayAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "3"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get all adjustments except PR-1 (deductible), PR-2 (coinsurance), PR-3 (copay)
  // These are shown in separate columns, so we exclude them from the Adjustments column
  const getOtherAdjustments = (rowData: ILineItem): ILineItemAdjustment[] => {
    return (
      rowData.adjustments?.filter(
        (adj) => !(adj.group === "PR" && ["1", "2", "3"].includes(adj.code))
      ) || []
    );
  };

  // Render adjustments cell (all adjustments except deductible/coinsurance/copay)
  // Also includes remark codes on separate lines
  const adjustmentsTemplate = (rowData: ILineItem) => {
    const otherAdjs = getOtherAdjustments(rowData);
    const remarks = rowData.remarks || [];

    if (otherAdjs.length === 0 && remarks.length === 0) return "-";

    return (
      <AdjustmentsCell>
        {otherAdjs.map((adj, idx) => (
          <AdjustmentLine key={`adj-${idx}`}>
            {formatAdjustment(adj.group, adj.code, adj.amount)}
          </AdjustmentLine>
        ))}
        {remarks.map((rem, idx) => (
          <AdjustmentLine key={`rem-${idx}`} style={{ color: "#6c757d" }}>
            {rem.code}
          </AdjustmentLine>
        ))}
      </AdjustmentsCell>
    );
  };

  // Collect all unique adjustment codes used across all line items for glossary
  const glossaryItems = useMemo(() => {
    const codeMap = new Map<string, { group: string; code: string }>();

    lineItems.forEach((item) => {
      item.adjustments?.forEach((adj) => {
        const key = `${adj.group}-${adj.code}`;
        if (!codeMap.has(key)) {
          codeMap.set(key, { group: adj.group, code: adj.code });
        }
      });
    });

    // Sort by group then code
    return Array.from(codeMap.values()).sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return a.code.localeCompare(b.code);
    });
  }, [lineItems]);

  // Collect all unique remark codes for glossary
  const remarkItems = useMemo(() => {
    const remarkSet = new Set<string>();

    lineItems.forEach((item) => {
      item.remarks?.forEach((rem) => {
        remarkSet.add(rem.code);
      });
    });

    return Array.from(remarkSet).sort();
  }, [lineItems]);

  const renderLineItemsSection = () => {
    if (
      claim.patientClass === "INPATIENT" &&
      lineItems.length === 0 &&
      claim.claimSummaryData
    ) {
      return <InpatientClaimSummary claim={claim} />;
    }

    if (lineItems.length > 0) {
      return (
        <>
          <DataTable
            value={lineItems}
            stripedRows
            responsiveLayout="scroll"
            emptyMessage="No line items available"
            style={{ fontSize: "0.9rem" }}
          >
            <Column
              field="cptCode"
              header="Code"
              style={{ minWidth: "5rem" }}
            />
            <Column
              field="modifier"
              header="Mod"
              body={(rowData) => rowData.modifier || "-"}
              style={{ minWidth: "4rem" }}
            />
            <Column
              field="revenueCode"
              header="Rev"
              body={(rowData) => rowData.revenueCode || "-"}
              style={{ minWidth: "4rem" }}
            />
            <Column
              field="billedUnits"
              header="Units"
              style={{ minWidth: "4rem" }}
            />
            <Column
              field="lineCharge"
              header="Billed"
              body={(rowData) => currencyBodyTemplate(rowData, "lineCharge")}
              style={{ minWidth: "6rem" }}
            />
            <Column
              field="lineAllowed"
              header="Allowed"
              body={(rowData) => currencyBodyTemplate(rowData, "lineAllowed")}
              style={{ minWidth: "6rem" }}
            />
            <Column
              header="Deduct"
              body={getDeductibleAmount}
              style={{ minWidth: "5rem" }}
            />
            <Column
              header="Coins"
              body={getCoinsuranceAmount}
              style={{ minWidth: "5rem" }}
            />
            <Column
              header="Copay"
              body={getCopayAmount}
              style={{ minWidth: "5rem" }}
            />
            <Column
              header="Adjustments"
              body={adjustmentsTemplate}
              style={{ minWidth: "10rem" }}
            />
            <Column
              field="linePaid"
              header="Paid"
              body={(rowData) => currencyBodyTemplate(rowData, "linePaid")}
              style={{ minWidth: "6rem" }}
            />
          </DataTable>

          {/* Glossary Section - shows explanations for codes used in this claim */}
          {glossaryItems.length > 0 && (
            <GlossarySection>
              <GlossaryTitle>Adjustment Codes</GlossaryTitle>
              {glossaryItems.map(({ group, code }) => {
                const description = CARC_CODES[code];
                const groupDesc = GROUP_CODES[group];
                return (
                  <GlossaryItem key={`${group}-${code}`}>
                    <GlossaryCode>
                      {group}-{code}
                    </GlossaryCode>
                    <GlossaryDescription>
                      {description || `${groupDesc || group} adjustment`}
                    </GlossaryDescription>
                  </GlossaryItem>
                );
              })}

              {/* Remark codes if any */}
              {remarkItems.length > 0 && (
                <>
                  <GlossaryTitle style={{ marginTop: "1rem" }}>
                    Remark Codes
                  </GlossaryTitle>
                  {remarkItems.map((code) => {
                    const description = RARC_CODES[code];
                    return (
                      <GlossaryItem key={code}>
                        <GlossaryCode>{code}</GlossaryCode>
                        <GlossaryDescription>
                          {description || "Remark code"}
                        </GlossaryDescription>
                      </GlossaryItem>
                    );
                  })}
                </>
              )}
            </GlossarySection>
          )}
        </>
      );
    }

    return <PlaceholderText>No line item data available</PlaceholderText>;
  };

  // Custom header with back button when navigation history exists
  const dialogHeader = (
    <ModalHeader>
      {onBackToClaim && (
        <BackButton
          icon="pi pi-arrow-left"
          className="p-button-text p-button-secondary"
          onClick={onBackToClaim}
          tooltip="Back to previous claim"
          tooltipOptions={{ position: "bottom" }}
        />
      )}
      <span>
        Claim Details - <span data-pii="patient-name">{claim.patientName}</span>
        {claim.claimNumber ? ` (${claim.claimNumber})` : ""}
      </span>
    </ModalHeader>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onHide}
      style={{ width: "95vw", maxWidth: "1800px" }}
      modal
      dismissableMask
    >
      <ModalContentWrapper>
        {isNavigating && (
          <LoadingOverlay>
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="4"
            />
          </LoadingOverlay>
        )}
        <TabView>
          {/* Tab 1: Claim Details */}
          <TabPanel header="Claim Details">
            <ScrollableTabContent ref={scrollContainerRef}>
              <div style={{ padding: "1rem" }}>
                <Tooltip
                  target=".adj-code-tooltip"
                  position="top"
                  showDelay={0}
                  style={{ zIndex: 10000 }}
                />
                <DetailGrid>
                  {/* Left Column - Patient Information */}
                  <div>
                    <h3
                      style={{
                        marginTop: 0,
                        marginBottom: "1rem",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Patient Information
                    </h3>
                    <DetailRow>
                      <DetailLabel>Patient Name:</DetailLabel>
                      <DetailValue data-pii="patient-name">
                        {claim.patientName}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Patient DOB:</DetailLabel>
                      <DetailValue>{formatDate(claim.patientDOB)}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Account #:</DetailLabel>
                      <DetailValue>
                        {claim.patientAccountNumber || "-"}
                      </DetailValue>
                    </DetailRow>

                    <h3
                      style={{
                        marginTop: "1.5rem",
                        marginBottom: "1rem",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Claim Information
                    </h3>
                    <DetailRow>
                      <DetailLabel>Claim #:</DetailLabel>
                      <DetailValue>{claim.claimNumber || "-"}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Service Date:</DetailLabel>
                      <DetailValue>{formatDate(claim.serviceDate)}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Payer Name:</DetailLabel>
                      <DetailValue>{claim.claimPayerName}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Group #:</DetailLabel>
                      <DetailValue>
                        {claim.claimPayerGroupNumber || "-"}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Network Status:</DetailLabel>
                      <DetailValue>
                        {claim.claimNetworkStatus || "-"}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Matched Payer:</DetailLabel>
                      <DetailValue>{claim.clearPayerName || "-"}</DetailValue>
                    </DetailRow>
                    {claim.patientClass && (
                      <DetailRow>
                        <DetailLabel>Claim Type:</DetailLabel>
                        <DetailValue>
                          <PatientClassBadge
                            patientClass={claim.patientClass}
                          />
                          {claim.typeOfBill && (
                            <span
                              style={{
                                marginLeft: "0.5rem",
                                fontSize: "0.8rem",
                                color: "#6c757d",
                              }}
                            >
                              TOB: {claim.typeOfBill}
                            </span>
                          )}
                        </DetailValue>
                      </DetailRow>
                    )}
                  </div>

                  {/* Right Column - Financial Summary */}
                  <div>
                    <h3
                      style={{
                        marginTop: 0,
                        marginBottom: "1rem",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      Financial Summary
                    </h3>
                    <DetailRow>
                      <DetailLabel>Case Signature:</DetailLabel>
                      <DetailValue>{claim.caseSignature}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Total Charges:</DetailLabel>
                      <DetailValue>
                        {formatCurrency(claim.chargeAmount)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Claim Amount:</DetailLabel>
                      <DetailValue>
                        {formatCurrency(claim.claimAmount)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Expected Amount:</DetailLabel>
                      <DetailValue>
                        {formatCurrency(claim.expectedAmount)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Difference:</DetailLabel>
                      <DetailValue
                        style={
                          claim.amountDifference && claim.amountDifference < 0
                            ? { fontWeight: "bold", color: "#dc3545" }
                            : undefined
                        }
                      >
                        {formatCurrency(claim.amountDifference)}
                      </DetailValue>
                    </DetailRow>
                  </div>
                </DetailGrid>

                <h3
                  style={{
                    marginTop: "1.5rem",
                    marginBottom: "1rem",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  {claim.patientClass === "INPATIENT" &&
                  lineItems.length === 0 &&
                  claim.claimSummaryData
                    ? "Claim Summary"
                    : "Line Items"}
                </h3>
                {renderLineItemsSection()}

                {/* 837 Claim Submission Data */}
                {claim.matched837Claims &&
                  claim.matched837Claims.length > 0 && (
                    <Matched837ClaimsSection claims={claim.matched837Claims} />
                  )}

                {/* Similar Correctly Paid Claims Section - only for claims with line items */}
                {lineItems.length > 0 && (
                  <SimilarClaimsSection
                    claimId={claim.id}
                    onNavigateToClaim={onNavigateToClaim}
                  />
                )}
              </div>
            </ScrollableTabContent>
          </TabPanel>

          {/* Tab 2: Allowable Calculations */}
          <TabPanel header="Expected Amounts">
            <ScrollableTabContent>
              {/* 835-based expected amount */}
              {claim.matched837Claims?.some(
                (c) => c.isAuthoritative && c.caseSignature
              ) && (
                <h4
                  style={{
                    margin: "0.5rem 1rem 0.5rem",
                    fontSize: "14px",
                    color: "#495057",
                  }}
                >
                  Expected Amount (835 Case Signature)
                </h4>
              )}
              {claim.payerId && claim.expectedAmount !== null ? (
                <PrecalculatedAllowablesContent
                  accountId={accountId}
                  payerId={claim.payerId}
                  caseSignature={claim.caseSignature}
                  lineItems={lineItems}
                />
              ) : (
                <PlaceholderText>
                  Allowable calculations not available for this claim.
                </PlaceholderText>
              )}

              {/* 837 DRG-based expected amount */}
              {claim.matched837Claims?.some(
                (c) => c.isAuthoritative && c.caseSignature
              ) && (
                <>
                  <h4
                    style={{
                      margin: "1.5rem 1rem 0.5rem",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    Expected Amount (837 DRG Case Signature)
                  </h4>
                  {claim.edi837ExpectedAmount !== null &&
                  claim.edi837ExpectedAmount !== undefined &&
                  claim.payerId ? (
                    <PrecalculatedAllowablesContent
                      accountId={accountId}
                      payerId={claim.payerId}
                      caseSignature={
                        claim.matched837Claims!.find((c) => c.isAuthoritative)!
                          .caseSignature!
                      }
                    />
                  ) : (
                    <PlaceholderText>
                      DRG-based allowable calculations not available. No
                      contract data found for this DRG code.
                    </PlaceholderText>
                  )}
                </>
              )}
            </ScrollableTabContent>
          </TabPanel>

          {/* Tab 3: AI Insights */}
          <TabPanel header="AI Insights">
            <ScrollableTabContent>
              <AIInsightsTabContent
                claimId={claim.id}
                cachedAnalysis={claim.aiAnalysisResult || null}
                promptVersion={claim.promptVersion}
                resolutionStatus={claim.resolutionStatus}
                resolutionHistory={claim.resolutionHistory}
                claimData={{
                  patientName: claim.patientName,
                  claimNumber: claim.claimNumber,
                  serviceDate: claim.serviceDate,
                  claimPayerName: claim.claimPayerName,
                  chargeAmount: claim.chargeAmount,
                }}
              />
            </ScrollableTabContent>
          </TabPanel>

          {/* Tab 4: Estimate Comparison */}
          <TabPanel header="Estimate Comparison">
            <ScrollableTabContent>
              <EstimateComparisonTab claim={claim} />
            </ScrollableTabContent>
          </TabPanel>
        </TabView>
      </ModalContentWrapper>
    </Dialog>
  );
}
